import { IPosition } from './common.types';
import { ISpriteClip } from './sprite.types';
import type { ProjectileConfig } from './projectile.types';
import type { SpellEffect } from './spell.types';
import {
  ActionDirectionType,
  AIBehaviorState,
  DungeonEntityType,
  EffectShape,
  Element,
  LagType,
  RangeAlgorithm,
} from './enums';

export interface IEntityStats {
  /** Max HP. Changes sync `entity.maxLife` on the handler side (cf. maxShield). */
  maxLife: number;
  physicalAttack: number;
  physicalDefense: number;
  magicAttack: number;
  magicDefense: number;
  moveSpeed: number; // % — 100 = normal speed
  size: number; // % — 100 = normal size
  maxShield: number;
  /**
   * Shield regen rate: shield points regenerated per second (default 0).
   * Tick handled by the buff system: each second, if shield < maxShield,
   * shield += shieldRegen.
   */
  shieldRegen: number;
  spellCastTime: number; // global multiplier for all spell cast/action/lag phases. 1.0 = normal, < 1 = faster, floor 0.05
  spellCooldown: number; // multiplier applied to a spell's cooldown when it starts. 1.0 = normal, > 1 = longer
  critChancePhysical: number; // 0–100, cap 100
  critChanceMagic: number; // 0–100, cap 100
  critMultiplier: number; // default 1.5 (150%)
  pickupReach: number; // euclidean pickup distance
  viewRange: number; // fog-of-war discovery radius
  opacity: number;
  /** HP regenerated per second when out of combat (no damage taken in the last 3000 ms). Default 0. */
  lifeRegenPerSecond: number;
  /** Number of inventory slots available to the player. Default 20. */
  inventorySlots: number;
}

export const DEFAULT_ENTITY_STATS: IEntityStats = {
  maxLife: 1,
  physicalAttack: 0,
  physicalDefense: 0,
  magicAttack: 0,
  magicDefense: 0,
  moveSpeed: 100,
  size: 100,
  maxShield: 0,
  shieldRegen: 0,
  spellCastTime: 1,
  spellCooldown: 1,
  critChancePhysical: 0,
  critChanceMagic: 0,
  critMultiplier: 1.5,
  pickupReach: 0,
  viewRange: 5,
  opacity: 1,
  lifeRegenPerSecond: 0,
  inventorySlots: 20,
};

export interface DungeonEntityProps {
  type: DungeonEntityType;
  life?: number;
  maxLife: number;
  isWalkableOver: boolean;
  targetable?: boolean;
  /** If false, knockback effects are ignored on this entity. Default true. */
  knockbackable?: boolean;
  configId: string;
  x: number;
  y: number;
  stats?: Partial<IEntityStats>;
  shield?: number;
  level?: number;
}

export interface IActionTimings {
  castTime: number;
  castBlocking: LagType;
  castSpriteName?: string;
  castAnimationSpeed?: number | 'auto';
  actionTime: number;
  actionBlocking: LagType;
  actionSpriteName?: string;
  actionAnimationSpeed?: number | 'auto';
  lagTime: number;
  lagBlocking: LagType;
  lagSpriteName?: string;
  lagAnimationSpeed?: number | 'auto';
}

export interface IDamageDisplay {
  id: string;
  damage: number;
  worldX: number;
  worldY: number;
  type: 'damage' | 'shieldDamage';
  crit?: boolean;
  element?: Element;
}

export interface IHealDisplay {
  id: string;
  heal: number;
  worldX: number;
  worldY: number;
  type: 'healHp' | 'healShield';
}

export type ActionParameter =
  | { type: 'direction' }
  | { type: 'position' }
  /** Self-targeted spell: no target, AOE/buff centered on the caster. */
  | { type: 'self' };

export interface IEffectShapeConfig {
  type: EffectShape;
  range?: number;
  customPattern?: IPosition[];
  /** CIRCLE only: skip the center cell (off by default — center is affected). */
  excludeCenter?: boolean;
}

export interface ActionDefinition {
  name: string;
  parameter?: ActionParameter;
  execute: (...args: number[]) => void;

  directionType?: ActionDirectionType;
  range?: number;
  minRange?: number;
  rangeAlgorithm?: RangeAlgorithm;
  lineOfSight?: boolean;
  shapes?: IEffectShapeConfig[];
  projectile?: ProjectileConfig;
  effects?: SpellEffect[];
  friendlyFire?: boolean;
  selfDamage?: boolean;
  ignoreFriendlyFire?: boolean;
  bypassDiagonalMiss?: boolean;
}

export interface IAffectedTilePreview {
  id: string;
  x: number;
  y: number;
  entityId?: string;
  duration?: number;
  elapsedTime?: number;
  type?: string;
  color?: number;
  alpha?: number;
  groupId?: string;
  /** Tile owned by the player → rendered with the player reticle frame. */
  isPlayer?: boolean;
  /** Skip the time-progress fill circle (static tint only, e.g. residual zones). */
  noProgressFill?: boolean;
}

export interface ICreateAffectedTileOptions {
  duration?: number;
  type?: string;
  color?: number;
  alpha?: number;
  groupId?: string;
  entityId?: string;
  isPlayer?: boolean;
  noProgressFill?: boolean;
}

export type AIArchetype = 'melee' | 'ranged' | 'flee';

export interface MonsterAIState {
  behaviorState: AIBehaviorState;
  lastDecisionTick: number;
  targetEntityId: string | null;
  aggroLevel: number;
  patrolPath?: IPosition[];
  lastKnownPlayerPosition?: IPosition;
  visionRange?: number;
  aggroDecayTicks?: number;
  /** Ticks remaining before the monster may start another offensive action. */
  actionCooldownTicks: number;
  /** Spell the monster is moving to get into range of (movement commitment).
   * Cast is opportunistic; this only governs positioning. Cleared on cast,
   * on target loss, or when the spell is no longer ready. */
  committedSpellId: string | null;
}

// Re-exported here for cross-reference (avoids importing from sprite.types).
export type { ISpriteClip };
